// Copyright 2024 Sorama B.V.

import { NamedColor } from "./NamedColor";

/** Just assigns colors to ids while trying to keep the colors as different as possible */
export class ColorAssigningStrategy {
    /**
     * Returns the next best color given what has already been assigned.
     * @param colorPalette The color palette (NamedColor[])to choose from.
     * @param assignedColors The colors that are already assigned to ids.
     * @returns The next best color
     */
    public static nextLeastOccuringColor(
        colorPalette: NamedColor[],
        assignedColors: { [id: string]: NamedColor }
    ): NamedColor {
        // Count the occurrence of each color in the colors object
        const occurrenceCounts: { [namedColorIndex: number]: number } = {};

        for (let i = 0; i < colorPalette.length; i++) {
            const candidateColor = colorPalette[i];
            const occurrenceCount = Object.keys(assignedColors).filter((id) =>
                assignedColors[id].color.equals(candidateColor.color)
            ).length;

            // If the color is not used, return it
            if (occurrenceCount === 0) {
                return candidateColor;
            }

            occurrenceCounts[i] = occurrenceCount;
        }

        // If all colours are taken, return the color with the least occurrence
        const leastOccuringColorIndex = Object.keys(occurrenceCounts).reduce((a, b) =>
            occurrenceCounts[a] < occurrenceCounts[b] ? a : b
        );

        return colorPalette[leastOccuringColorIndex];
    }
}
